import { EmptyState } from '@ifood/pomodoro-components';
import { useIntl } from 'gatsby-plugin-intl';
import React, { ReactNode } from 'react';
import styled from 'styled-components';

import EmptyStateImage from 'images/empty-state-bg.svg';
import { TablePlaceholder as DefaultLoading } from 'modules/UI';

export const ContentStyle = styled(EmptyState)`
  > img {
    width: 180px;
  }
`;

type EmptyTypes<T> = {
  filters?: T;
  isIdle?: boolean;
};

type ErrorTypes<T> = {
  error?: T;
};

type PropTypes<T> = {
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  isIdle?: boolean;
  filters?: T;
  error?: T;
  loadingPlaceholder?: React.FC | ReactNode;
  emptyPlaceholder?: React.FC<EmptyTypes<T>> | ReactNode;
  errorPlaceholder?: React.FC<ErrorTypes<T>> | ReactNode;
  content: React.FC | ReactNode;
};

const DefaultEmpty = <T,>({ filters }: EmptyTypes<T>): JSX.Element => {
  const { formatMessage } = useIntl();

  return (
    <ContentStyle
      title={formatMessage({ id: filters ? 'no-results-found' : 'no-results-empty' })}
      description={
        filters
          ? formatMessage({
              id: 'no-results-found-description',
            })
          : ''
      }
      imgSrc={EmptyStateImage}
      imgAlt={formatMessage({ id: filters ? 'no-results-found' : 'no-results-empty' })}
    />
  );
};

const DefaultError = (): JSX.Element => {
  const { formatMessage } = useIntl();

  return (
    <ContentStyle
      title={formatMessage({ id: 'empty-state.failed-to-load.title' })}
      description={formatMessage({
        id: 'empty-state.failed-to-load.description',
      })}
      imgSrc={EmptyStateImage}
      imgAlt={formatMessage({ id: 'empty-state.failed-to-load.title' })}
    />
  );
};

export const Listing = <T,>({
  isLoading,
  isError,
  isEmpty,
  isIdle,
  loadingPlaceholder: LoadingPlaceholder = DefaultLoading,
  emptyPlaceholder: EmptyPlaceholder = DefaultEmpty,
  errorPlaceholder: ErrorPlaceholder = DefaultError,
  content: Content,
  filters,
  error,
}: PropTypes<T>): JSX.Element => {
  const canShowContent = !isLoading && !isError && !isEmpty && !isIdle;

  if (isLoading) {
    return typeof LoadingPlaceholder === 'function' ? <LoadingPlaceholder /> : <>{LoadingPlaceholder}</>;
  }

  if (isEmpty || isIdle) {
    return typeof EmptyPlaceholder === 'function' ? (
      <EmptyPlaceholder filters={filters} isIdle={isIdle} />
    ) : (
      <>{EmptyPlaceholder}</>
    );
  }

  if (canShowContent) {
    return typeof Content === 'function' ? <Content /> : <>{Content}</>;
  }

  if (isError) {
    return typeof ErrorPlaceholder === 'function' ? <ErrorPlaceholder error={error} /> : <>{ErrorPlaceholder}</>;
  }

  return typeof ErrorPlaceholder === 'function' ? <ErrorPlaceholder error={undefined} /> : <>{ErrorPlaceholder}</>;
};
